import React from 'react';
import { Button, InputGroup, FormControl, Badge } from 'react-bootstrap';
import { FaFilter, FaSearch } from 'react-icons/fa';

/**
 * Component for message filtering controls
 */
const MessageFilters = ({
    // Status filter props
    selectedStatus,
    statusTypes,
    statusCounts,
    handleStatusFilter,
    messagesCount,
    
    // Customer filter props
    customerSearchText,
    handleCustomerSearchChange,
    showCustomerDropdown,
    setShowCustomerDropdown,
    filteredCustomers,
    selectedCustomerId,
    handleCustomerFilterChange,
    
    // Address filter props
    addressSearchText,
    setAddressSearchText,
    showAddressDropdown,
    setShowAddressDropdown,
    filteredAddresses,
    messages,
    setFilteredMessages,
    
    // Reservation ID filter props
    reservationIdSearchText,
    setReservationIdSearchText,
    showReservationIdDropdown,
    setShowReservationIdDropdown,
    filteredReservationIds,
    
    // Search props
    searchText,
    handleSearchChange
}) => {
    return (
        <div className="mb-3">
            <div className="d-flex mb-2 flex-wrap">
                {/* Status Filter */}
                <div className="me-3 mb-2 d-flex align-items-center">
                    <div className="me-2">
                        <small className="fw-bold">Status:</small>
                    </div>
                    <div className="d-flex">
                        <Button
                            variant={selectedStatus === "" ? "primary" : "outline-primary"}
                            size="sm"
                            className="me-1"
                            onClick={() => handleStatusFilter("")}
                        >
                            All
                            <Badge bg="secondary" className="ms-1">
                                {messagesCount}
                            </Badge>
                        </Button>
                        
                        {statusTypes.map((status, index) => {
                            let variant = 'secondary';
                            if (status === 'PROCESSED') variant = 'success';
                            if (status === 'FAILED' || status === 'ERROR') variant = 'danger';
                            if (status === 'PENDING') variant = 'warning';
                            
                            return (
                                <Button
                                    key={`status-${index}`}
                                    variant={selectedStatus === status ? variant : `outline-${variant}`}
                                    size="sm"
                                    className="me-1"
                                    onClick={() => handleStatusFilter(status)}
                                >
                                    {status}
                                    <Badge bg="secondary" className="ms-1">
                                        {statusCounts[status] || 0}
                                    </Badge>
                                </Button>
                            );
                        })}
                    </div>
                </div>
                
                {/* Customer Filter */}
                <div className="me-3" style={{ width: '300px' }}>
                    <div className="d-flex align-items-center mb-1">
                        <FaFilter className="me-1 text-primary" size={12} />
                        <small className="fw-bold">Filter by Customer</small>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <InputGroup size="sm">
                            <FormControl
                                placeholder="Type to search customers..."
                                value={customerSearchText}
                                onChange={handleCustomerSearchChange}
                                onFocus={() => setShowCustomerDropdown(true)}
                                aria-label="Customer search"
                                autoComplete="off"
                            />
                            <Button 
                                variant="outline-primary" 
                                onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                            >
                                ▼
                            </Button>
                        </InputGroup>
                        {showCustomerDropdown && (
                            <div 
                                className="dropdown-menu show w-100" 
                                style={{ 
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1000,
                                    maxHeight: '300px',
                                    overflow: 'auto'
                                }}
                            >
                                <div 
                                    className="dropdown-item" 
                                    onClick={() => {
                                        handleCustomerFilterChange("");
                                        setShowCustomerDropdown(false);
                                    }}
                                    style={{
                                        backgroundColor: selectedCustomerId === "" ? "#007bff" : "",
                                        color: selectedCustomerId === "" ? "white" : ""
                                    }}
                                >
                                    All Customers
                                </div>
                                {filteredCustomers.map((customer, index) => (
                                    <div 
                                        key={`customer-${index}-${customer.id}`}
                                        className="dropdown-item"
                                        onClick={() => {
                                            handleCustomerFilterChange(customer.id);
                                            setShowCustomerDropdown(false);
                                        }}
                                        style={{
                                            backgroundColor: selectedCustomerId === customer.id ? "#007bff" : "",
                                            color: selectedCustomerId === customer.id ? "white" : "",
                                            // Gray out customers with no messages
                                            ...(customer.messageCount === 0 && selectedCustomerId !== customer.id ? 
                                                { color: "#999", fontWeight: "normal" } : {})
                                        }}
                                    >
                                        {customer.name} {customer.messageCount > 0 && 
                                            <span className="ms-1 badge bg-secondary">
                                                {customer.messageCount}
                                            </span>
                                        }
                                    </div>
                                ))}
                                {filteredCustomers.length === 0 && customerSearchText && (
                                    <div className="dropdown-item disabled">No matches found</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Address Filter */}
                <div className="me-3" style={{ width: '300px' }}>
                    <div className="d-flex align-items-center mb-1">
                        <FaFilter className="me-1 text-primary" size={12} />
                        <small className="fw-bold">Filter by Address</small>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <InputGroup size="sm">
                            <FormControl
                                placeholder="Type to search addresses..."
                                value={addressSearchText}
                                onChange={(e) => {
                                    setAddressSearchText(e.target.value);
                                    setShowAddressDropdown(true);
                                }}
                                onFocus={() => setShowAddressDropdown(true)}
                                aria-label="Address search"
                                autoComplete="off"
                            />
                            <Button 
                                variant="outline-primary" 
                                onClick={() => setShowAddressDropdown(!showAddressDropdown)}
                            >
                                ▼
                            </Button>
                        </InputGroup>
                        {showAddressDropdown && filteredAddresses.length > 0 && (
                            <div 
                                className="dropdown-menu show w-100" 
                                style={{ 
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1000,
                                    maxHeight: '300px',
                                    overflow: 'auto'
                                }}
                            >
                                <div 
                                    className="dropdown-item" 
                                    onClick={() => {
                                        // Clear address filter
                                        setAddressSearchText("");
                                        setShowAddressDropdown(false);
                                        
                                        // Apply other filters to the current messages
                                        let filteredResult = messages;
                                        
                                        // Apply customer filter if one is selected
                                        if (selectedCustomerId) {
                                            filteredResult = filteredResult.filter(message => 
                                                message.cosCustomerId === selectedCustomerId
                                            );
                                        }
                                        
                                        // Apply status filter if one is selected
                                        if (selectedStatus) {
                                            filteredResult = filteredResult.filter(message => 
                                                message.processingStatus === selectedStatus
                                            );
                                        }
                                        
                                        // Apply text search filter
                                        if (searchText) {
                                            // This would call the parent's applyFilters function
                                            // For now, we'll just set the filtered messages
                                            setFilteredMessages(filteredResult);
                                        } else {
                                            setFilteredMessages(filteredResult);
                                        }
                                    }}
                                >
                                    All Addresses
                                </div>
                                {filteredAddresses.map((address, index) => (
                                    <div 
                                        key={`address-${index}`}
                                        className="dropdown-item"
                                        onClick={() => {
                                            setAddressSearchText(address);
                                            setShowAddressDropdown(false);
                                            
                                            // Filter messages by the selected address
                                            const addressFiltered = messages.filter(
                                                message => message.listingAddress === address
                                            );
                                            setFilteredMessages(addressFiltered);
                                        }}
                                        style={{
                                            backgroundColor: addressSearchText === address ? "#007bff" : "",
                                            color: addressSearchText === address ? "white" : ""
                                        }}
                                    >
                                        {address}
                                    </div>
                                ))}
                                {filteredAddresses.length === 0 && addressSearchText && (
                                    <div className="dropdown-item disabled">No matches found</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Reservation ID Filter */}
                <div className="me-3" style={{ width: '300px' }}>
                    <div className="d-flex align-items-center mb-1">
                        <FaFilter className="me-1 text-primary" size={12} />
                        <small className="fw-bold">Filter by Reservation ID</small>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <InputGroup size="sm">
                            <FormControl
                                placeholder="Type to search reservation IDs..."
                                value={reservationIdSearchText}
                                onChange={(e) => {
                                    setReservationIdSearchText(e.target.value);
                                    setShowReservationIdDropdown(true);
                                }}
                                onFocus={() => setShowReservationIdDropdown(true)}
                                aria-label="Reservation ID search"
                                autoComplete="off"
                            />
                            <Button 
                                variant="outline-primary" 
                                onClick={() => setShowReservationIdDropdown(!showReservationIdDropdown)}
                            >
                                ▼
                            </Button>
                        </InputGroup>
                        {showReservationIdDropdown && filteredReservationIds.length > 0 && (
                            <div 
                                className="dropdown-menu show w-100" 
                                style={{ 
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1000,
                                    maxHeight: '300px',
                                    overflow: 'auto'
                                }}
                            >
                                <div 
                                    className="dropdown-item" 
                                    onClick={() => {
                                        // Clear reservation ID filter
                                        setReservationIdSearchText("");
                                        setShowReservationIdDropdown(false);
                                        
                                        // Apply other filters to the current messages
                                        let filteredResult = messages;
                                        
                                        // Apply customer filter if one is selected
                                        if (selectedCustomerId) {
                                            filteredResult = filteredResult.filter(message => 
                                                message.cosCustomerId === selectedCustomerId
                                            );
                                        }
                                        
                                        // Apply status filter if one is selected
                                        if (selectedStatus) {
                                            filteredResult = filteredResult.filter(message => 
                                                message.processingStatus === selectedStatus
                                            );
                                        }
                                        
                                        // Apply address filter if one is selected
                                        if (addressSearchText) {
                                            filteredResult = filteredResult.filter(message => 
                                                message.listingAddress === addressSearchText
                                            );
                                        }
                                        
                                        // Apply text search filter
                                        if (searchText) {
                                            // This would call the parent's applyFilters function
                                            // For now, we'll just set the filtered messages
                                            setFilteredMessages(filteredResult);
                                        } else {
                                            setFilteredMessages(filteredResult);
                                        }
                                    }}
                                >
                                    All Reservation IDs
                                </div>
                                {filteredReservationIds.map((reservationId, index) => (
                                    <div 
                                        key={`reservation-id-${index}`}
                                        className="dropdown-item"
                                        onClick={() => {
                                            setReservationIdSearchText(reservationId);
                                            setShowReservationIdDropdown(false);
                                            
                                            // Filter messages by the selected reservation ID
                                            const reservationIdFiltered = messages.filter(
                                                message => message.reservationId === reservationId
                                            );
                                            setFilteredMessages(reservationIdFiltered);
                                        }}
                                        style={{
                                            backgroundColor: reservationIdSearchText === reservationId ? "#007bff" : "",
                                            color: reservationIdSearchText === reservationId ? "white" : ""
                                        }}
                                    >
                                        {reservationId}
                                    </div>
                                ))}
                                {filteredReservationIds.length === 0 && reservationIdSearchText && (
                                    <div className="dropdown-item disabled">No matches found</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Search */}
                <div style={{ width: '300px' }}>
                    <div className="d-flex align-items-center mb-1">
                        <FaSearch className="me-1 text-primary" size={12} />
                        <small className="fw-bold">Search</small>
                    </div>
                    <InputGroup size="sm">
                        <FormControl
                            value={searchText}
                            onChange={handleSearchChange}
                            type="search"
                            placeholder="Search messages..."
                            aria-label="Search"
                            autoComplete="off"
                        />
                    </InputGroup>
                </div>
            </div>
        </div>
    );
};

export default MessageFilters;
